/**
 * Modal, confirmation dialog and the typed-phrase confirmation.
 *
 * `ConfirmModal` takes a `tone`, and `danger` deliberately costs more
 * clicks. `TypedConfirmModal` goes further and requires an exact phrase —
 * that is what re-arming a tripped kill-switch uses, mirroring the
 * backend's `rearm_confirmation_phrase`. Friction is the feature.
 *
 * What must never be built on top of this: a dialog that cancels a
 * protective stop without replacing it. That affordance is the one this
 * platform exists to remove.
 */

import { AlertTriangle, X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/cn';
import { meetsReasonMinimum, visibleCharacters, visibleLength } from '@/lib/reasons';

import { Button } from './Button';
import { Input, Textarea } from './Input';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'wide';

const SIZES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-5xl',
  wide: 'max-w-[1800px]',
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  /** Escape and backdrop clicks stop closing it. For irreversible flows. */
  dismissible?: boolean;
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);
  // `onClose` is read through a ref so the focus-trap effect below is keyed on
  // `open`/`dismissible` ONLY. Every caller passes an inline arrow, which is a
  // new function on each render — and the pages behind these dialogs re-render
  // every few seconds (pollers) or on every keystroke (a reason textarea that
  // lifts its value to the parent). Keyed on `onClose`, the effect tore down
  // and re-ran on each of those renders: the cleanup handed focus back to the
  // element behind the dialog, the re-run's timer focused the first focusable
  // in the panel (the header's close button), and the field the owner was
  // typing in lost focus after every character. That was the "one character
  // at a time" textbox (owner, 2026-09-01).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && dismissible) {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      // Trap focus. Without it, Tab walks into the page behind the modal and
      // the next Enter presses a button the user cannot see.
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const timer = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      // Prefer the first field in the BODY — the thing the dialog is asking
      // for — then the footer's action, and only then the header's close
      // button, which sits first in document order but is never what a
      // freshly opened dialog wants focused.
      const fields =
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])';
      const body = panel.querySelector<HTMLElement>('[data-modal-body]');
      const target =
        body?.querySelector<HTMLElement>(fields) ??
        body?.querySelector<HTMLElement>('button:not([disabled])') ??
        panel.querySelector<HTMLElement>('[data-modal-footer] button:not([disabled])') ??
        panel.querySelector<HTMLElement>(`${fields}, button:not([disabled])`);
      target?.focus();
    }, 0);

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      window.clearTimeout(timer);
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [open, dismissible]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-0 sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'slide-in flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-lg border border-border',
          'bg-surface-1 shadow-[var(--shadow-pop)] sm:rounded-lg',
          SIZES[size],
          className,
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[14px] font-semibold text-hi">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-[12px] text-mid">{description}</p>}
          </div>
          {dismissible && (
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              icon={X}
              onClick={onClose}
              aria-label="Close dialog"
            />
          )}
        </header>

        {children && (
          <div data-modal-body className="min-h-0 flex-1 overflow-auto px-4 py-3">
            {children}
          </div>
        )}

        {footer && (
          <footer
            data-modal-footer
            className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-4 py-3"
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  /** Say what will happen, in currency where money is involved. */
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'accent' | 'danger' | 'warn';
  loading?: boolean;
  /** Requires a written reason. The backend demands one on audited actions. */
  requireReason?: boolean;
  reasonMinLength?: number;
  reasonLabel?: string;
  onReasonChange?: (reason: string) => void;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'accent',
  loading = false,
  requireReason = false,
  reasonMinLength = 10,
  reasonLabel = 'Reason (kept in the audit log)',
  onReasonChange,
}: ConfirmModalProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  // Counted as the server counts it: visible characters only [G10].
  const reasonOk = !requireReason || meetsReasonMinimum(reason, reasonMinLength);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {tone !== 'accent' && (
            <AlertTriangle
              size={15}
              className={tone === 'danger' ? 'text-danger' : 'text-warn'}
              aria-hidden
            />
          )}
          {title}
        </span>
      }
      size="sm"
      dismissible={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : tone === 'warn' ? 'warn' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            disabled={!reasonOk}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[13px] text-hi">
        <div>{message}</div>
        {requireReason && (
          <Textarea
            label={reasonLabel}
            value={reason}
            showCount
            maxLength={2000}
            placeholder="What changed, and why this is the right call."
            hint={`At least ${visibleCharacters(reasonMinLength)}. This is kept forever.`}
            error={
              reason.length > 0 && !reasonOk
                ? `${visibleCharacters(reasonMinLength - visibleLength(reason))} more needed`
                : null
            }
            onChange={(event) => {
              setReason(event.target.value);
              onReasonChange?.(event.target.value);
            }}
          />
        )}
      </div>
    </Modal>
  );
}

export interface TypedConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  title: ReactNode;
  message: ReactNode;
  /** Must be typed back exactly. From the backend, never invented here. */
  phrase: string;
  confirmLabel?: string;
  loading?: boolean;
  reasonMinLength?: number;
  /** Consequences listed one per line above the input. */
  consequences?: string[];
}

/**
 * Typed-phrase confirmation.
 *
 * For re-arming a kill-switch and for overriding a promotion gate: the two
 * places where a reflex click could undo the thing keeping the account
 * alive. The phrase comes from the backend response — copying it here would
 * be a second source of truth for a safety control.
 */
export function TypedConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  phrase,
  confirmLabel = 'Confirm',
  loading = false,
  reasonMinLength = 20,
  consequences,
}: TypedConfirmModalProps) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  const phraseOk = typed === phrase;
  const reasonOk = meetsReasonMinimum(reason, reasonMinLength);

  const confirm = useCallback(() => {
    if (phraseOk && reasonOk) onConfirm(reason.trim());
  }, [phraseOk, reasonOk, onConfirm, reason]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <AlertTriangle size={15} className="text-danger" aria-hidden />
          {title}
        </span>
      }
      size="md"
      dismissible={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            loading={loading}
            disabled={!phraseOk || !reasonOk}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[13px]">
        <div className="text-hi">{message}</div>

        {consequences && consequences.length > 0 && (
          <ul className="space-y-1 rounded border border-danger bg-danger-weak px-3 py-2 text-[12px] text-hi">
            {consequences.map((line) => (
              <li key={line} className="flex gap-2">
                <span className="text-danger" aria-hidden>
                  •
                </span>
                {line}
              </li>
            ))}
          </ul>
        )}

        <Textarea
          label="Why is this the right decision?"
          value={reason}
          showCount
          maxLength={4000}
          placeholder="What changed since the breach. A one-word reason is not a review."
          hint={`At least ${visibleCharacters(reasonMinLength)}. Kept forever in the audit log.`}
          onChange={(event) => setReason(event.target.value)}
        />

        <Input
          label={
            <>
              Type <code className="num rounded bg-surface-3 px-1 text-hi">{phrase}</code> to
              confirm
            </>
          }
          value={typed}
          autoComplete="off"
          spellCheck={false}
          className="num"
          onChange={(event) => setTyped(event.target.value)}
          error={typed.length > 0 && !phraseOk ? 'The phrase does not match exactly.' : null}
        />
      </div>
    </Modal>
  );
}
